import { GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/format";
import { leadDisplayName } from "@/lib/contact";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { relativeFrom } from "../types";
import type { SolicitudTicket } from "./types";

type Props = {
  ticket: SolicitudTicket;
  selected?: boolean;
  draggable?: boolean;
  onClick: () => void;
  employeeNameById: Map<string, string>;
};

// Priority pills in the Tablero palette. The Actividades board uses raw
// Tailwind reds/oranges; these map onto the semantic tokens instead.
const PRIORITY_PILL: Record<string, string> = {
  urgent: "bg-danger-light text-danger",
  high: "bg-warning-light text-neutral-900",
  medium: "bg-neutral-100 text-neutral-600",
  low: "bg-palomma-primary/10 text-palomma-primary",
};

const PRIORITY_LABEL: Record<string, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
};

// Ticket analogue of QuienAtiendeBadge — same visual grammar (dot or initials
// avatar + muted caption), ticket-appropriate copy.
const GestionBadge = ({ isAi, asesor }: { isAi: boolean; asesor?: string }) => (
  <span className="inline-flex items-center gap-1.5 text-xs">
    {isAi ? (
      <>
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inset-0 animate-ping rounded-full bg-success opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
        </span>
        <span className="text-neutral-600">Gestionado por IA</span>
      </>
    ) : (
      <>
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-palomma-primary text-[9px] font-semibold text-white">
          {asesor ? initials(asesor) : "A"}
        </span>
        <span className="truncate text-neutral-600">{asesor ?? "Asesor"} gestionando</span>
      </>
    )}
  </span>
);

const SolicitudCard = ({ ticket, selected, draggable, onClick, employeeNameById }: Props) => {
  // useSortable must be called unconditionally — when dragging is disabled we
  // just don't render the handle. Hook order stays stable.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ticket.id,
    data: { status: ticket.status },
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // Urgency is this board's equivalent of the lead board's "requiere atención
  // humana" — the one state that should pull the eye across a full column.
  const isUrgent = ticket.priority === "urgent";
  const isAi = ticket.managedBy === "ai";
  const asesor = ticket.assignedEmployeeId
    ? employeeNameById.get(ticket.assignedEmployeeId)
    : undefined;
  // Resolved server-side against the live Lead — see the note in TicketCard.
  const displayName = leadDisplayName({ name: ticket.leadName });
  const contact = ticket.leadPhone;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        "relative cursor-pointer rounded-md border bg-surface p-3 shadow-card transition-shadow hover:shadow-md",
        selected
          ? "border-2 border-palomma-primary bg-palomma-primary/5"
          : isUrgent
            ? "border-danger ring-1 ring-danger/30"
            : "border-transparent"
      )}
    >
      {isUrgent && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="absolute left-2 top-2 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-danger" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">Prioridad urgente</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {draggable ? (
        <button
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="absolute right-2 top-2 cursor-grab text-neutral-300 hover:text-neutral-600 active:cursor-grabbing"
          aria-label="Mover"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      ) : null}
      <div
        className={cn("truncate pr-6 text-sm font-semibold text-neutral-900", isUrgent && "pl-4")}
      >
        {displayName}
      </div>
      {contact ? (
        <div className="mt-0.5 truncate text-xs text-neutral-600">{contact}</div>
      ) : null}
      <div className="my-2 border-t border-neutral-100" />
      <div className="flex items-center gap-2 text-xs">
        {ticket.category ? (
          <span className="truncate rounded-pill bg-palomma-primary/10 px-2 py-0.5 text-[11px] font-medium text-palomma-primary">
            {ticket.category}
          </span>
        ) : null}
        <span
          className={cn(
            "shrink-0 rounded-pill px-2 py-0.5 text-[11px] font-medium",
            PRIORITY_PILL[ticket.priority] ?? PRIORITY_PILL.medium
          )}
        >
          {PRIORITY_LABEL[ticket.priority] ?? ticket.priority}
        </span>
        <span className="shrink-0 text-neutral-600">· {relativeFrom(ticket.createdAt)}</span>
      </div>
      <div className="my-2 border-t border-neutral-100" />
      <div className="mb-2">
        <GestionBadge isAi={isAi} asesor={asesor} />
      </div>
      {ticket.description ? (
        <div className="truncate text-xs italic text-neutral-600">{ticket.description}</div>
      ) : null}
    </div>
  );
};

export default SolicitudCard;
